import type { ProjectMoveResult } from "./api.ts";

export const NO_PROJECT_LABEL = "No project";

interface ProjectLike {
  id: string;
  name: string;
}

/** The project every item is in (null = no project), or undefined when they differ or nothing is given. */
export function sharedProjectId(items: { project_id?: string | null }[]): string | null | undefined {
  if (items.length === 0) return undefined;
  const first = items[0].project_id ?? null;
  return items.every((item) => (item.project_id ?? null) === first) ? first : undefined;
}

export function projectName(projects: ProjectLike[] | undefined, id: string | null): string {
  if (id === null) return NO_PROJECT_LABEL;
  return projects?.find((project) => project.id === id)?.name ?? "another project";
}

/** Case- and accent-insensitive name filter ("ulker" finds "Ülker"). */
export function filterProjects<T extends ProjectLike>(projects: T[], query: string): T[] {
  const fold = (value: string) => value.toLocaleLowerCase("tr").normalize("NFD").replace(/\p{M}/gu, "").replace(/ı/g, "i");
  const needle = fold(query.trim());
  return needle ? projects.filter((project) => fold(project.name).includes(needle)) : projects;
}

export function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += size) chunks.push(ids.slice(index, index + size));
  return chunks;
}

export function mergeMoveResults(results: ProjectMoveResult[]): ProjectMoveResult {
  return {
    project_id: results[0]?.project_id ?? null,
    moved: results.flatMap((result) => result.moved),
    unchanged: results.flatMap((result) => result.unchanged),
    skipped: results.flatMap((result) => result.skipped),
  };
}

/** Undo moves each item back to where it came from, one request per original project. */
export function undoGroups(result: ProjectMoveResult): { projectId: string | null; ids: string[] }[] {
  const groups = new Map<string | null, string[]>();
  for (const item of result.moved) {
    const ids = groups.get(item.from_project_id) ?? [];
    ids.push(item.id);
    groups.set(item.from_project_id, ids);
  }
  return [...groups].map(([projectId, ids]) => ({ projectId, ids }));
}

function items(count: number): string {
  return `${count} ${count === 1 ? "item" : "items"}`;
}

export function moveSummary(result: ProjectMoveResult, targetName: string, extraSkipped = 0): string {
  const count = (reason: string) => result.skipped.filter((item) => item.reason === reason).length;
  const busy = count("batch_preparing");
  const hidden = count("owner_cannot_see_project");
  const notYours = result.skipped.length - busy - hidden + extraSkipped;
  const parts: string[] = [];
  if (result.moved.length > 0) {
    parts.push(result.project_id === null ? `Removed ${items(result.moved.length)} from their project` : `Moved ${items(result.moved.length)} to ${targetName}`);
  }
  if (result.unchanged.length > 0) {
    parts.push(result.moved.length > 0 ? `${result.unchanged.length} already there` : `${items(result.unchanged.length)} already in ${targetName}`);
  }
  if (notYours > 0) parts.push(`${notYours} not yours, skipped`);
  if (busy > 0) parts.push(`${busy} still generating in a batch, skipped`);
  if (hidden > 0) parts.push(`${hidden} skipped: their owner can't open ${targetName}`);
  return parts.join(" · ") || "Nothing was moved";
}
